import { useEffect, useState, useCallback } from "react";
import PropTypes from "prop-types";
import update from "immutability-helper";

import TableContainer from "@mui/material/TableContainer";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import Button from "@mui/material/Button";
import AddCircle from "@mui/icons-material/AddCircle";

import { MetaTableContent } from "components/CaseManagement/CaseDetail/MetaDataTable/MetaTableContent";
import { MetaDataAdditionDialog } from "components/CaseManagement/CaseDetail/MetaDataTable/MetaDataAdditionDialog";
import { getSanitizedAttributeValue } from "components/CaseManagement/CaseDetail/MetaDataTable/meta_utils";

import useMetaDataTableStyles from "assets/jss/default/components/metaDataTableStyles";

export function MetaDataTable(props) {
  const { caseModel, variant, onMetaDataChanged } = props;

  const [addAttributeDialogOpen, setAddAttributeDialogOpen] = useState(false);

  const [displayedAttributes, setDisplayedAttributes] = useState(() => {
    if (caseModel?.attributes) {
      return Object.entries(caseModel.attributes).map(([key, values]) => ({
        [key]: {
          value: values.value,
          type_name: values.type_name,
          type_id: values.type_id
        }
      }));
    }
    return [];
  });

  const classes = useMetaDataTableStyles();

  useEffect(() => {
    if (caseModel?.attributes) {
      setDisplayedAttributes(
        Object.entries(caseModel.attributes).map(([key, values]) => ({
          [key]: {
            value: values.value,
            type_name: values.type_name,
            type_id: values.type_id
          }
        }))
      );
    } else {
      setDisplayedAttributes([]);
    }
  }, [caseModel?.id, caseModel?.attributes]);

  const handleAddAttributeDialogClose = () => {
    setAddAttributeDialogOpen(false);
  };

  const handleAttributeDeleted = useCallback(
    name => {
      const existingAttribute = caseModel.attributes[name];
      if (existingAttribute) {
        delete caseModel.attributes[name];
        const updatedAttributes = { ...caseModel.attributes };

        const newDisplayed = displayedAttributes.filter(
          attrObj => !Object.hasOwn(attrObj, name)
        );
        setDisplayedAttributes(newDisplayed);
        onMetaDataChanged({
          init: false,
          deleted: name,
          model: update(caseModel, {
            attributes: {
              $set: updatedAttributes
            }
          })
        });
      }
    },
    [caseModel, onMetaDataChanged, displayedAttributes]
  );

  const handleAttributeChanged = useCallback(
    ({ name, value, type_name, type_id }, data_type) => {
      const sanitizedValue = getSanitizedAttributeValue(value, data_type);
      const updatedAttributes = { ...caseModel.attributes };
      updatedAttributes[name] = { value: sanitizedValue, type_name, type_id };

      let mergedDisplayed = [...displayedAttributes];
      const idx = mergedDisplayed.findIndex(
        attrObj => Object.keys(attrObj)[0] === name
      );

      if (idx !== -1) {
        // Update existing
        mergedDisplayed[idx] = {
          [name]: {
            value: sanitizedValue,
            type_name,
            type_id
          }
        };
      } else {
        // Add new
        mergedDisplayed.push({
          [name]: {
            value: sanitizedValue,
            type_name,
            type_id
          }
        });
      }

      setDisplayedAttributes(mergedDisplayed);

      onMetaDataChanged({
        init: false,
        model: update(caseModel, {
          attributes: {
            $set: updatedAttributes
          }
        })
      });
    },
    [caseModel, onMetaDataChanged, displayedAttributes]
  );

  const handleAddButtonClick = () => {
    setAddAttributeDialogOpen(true);
  };

  const isEmpty = displayedAttributes.length === 0;

  if (!caseModel?.id) {
    return (
      <span>
        Meta data table doesn&apos;t support the provided case data. The table
        won&apos;t display here.
      </span>
    );
  }

  const flatAttributes = displayedAttributes.map(attrObj => {
    const key = Object.keys(attrObj)[0];
    const values = attrObj[key];
    return {
      name: key,
      ...values
    };
  });

  return (
    <>
      <div className={classes.root}>
        <Paper className={classes.paper} elevation={0}>
          <Button
            variant="outlined"
            color="primary"
            aria-label="Add Meta Attribute Button"
            onClick={handleAddButtonClick}
            startIcon={<AddCircle />}
            data-qa="CM-Button-Add-Attribute"
          >
            Add attribute
          </Button>
          <TableContainer>
            <Table
              className={classes.table}
              stickyHeader
              size={variant === "dense" ? "small" : "medium"}
              data-qa="CM-Table-Attributes"
            >
              <TableHead>
                <TableRow>
                  <TableCell data-col={1}>Attribute Name</TableCell>
                  <TableCell data-col={2}>Attribute Value</TableCell>
                  <TableCell data-col={3}></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {isEmpty ? (
                  <EmptyTablePadding />
                ) : (
                  flatAttributes.map(attr => (
                    <MetaTableContent
                      key={attr.name}
                      attributes={[attr]}
                      onAttributeDeleted={handleAttributeDeleted}
                      onAttributeChanged={handleAttributeChanged}
                    />
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
        {addAttributeDialogOpen ? (
          <MetaDataAdditionDialog
            caseModel={caseModel}
            open={addAttributeDialogOpen}
            onClose={handleAddAttributeDialogClose}
            onAttributeChanged={handleAttributeChanged}
          />
        ) : null}
      </div>
    </>
  );
}

MetaDataTable.propTypes = {
  caseModel: PropTypes.object,
  onMetaDataChanged: PropTypes.func.isRequired,
  variant: PropTypes.oneOf(["regular", "dense"])
};

MetaDataTable.defaultProps = {
  variant: "regular"
};

function EmptyTablePadding() {
  return (
    <>
      <TableRow style={{ height: 160 }}>
        <TableCell colSpan={2} align="center">
          No attribute entries yet.
        </TableCell>
      </TableRow>
    </>
  );
}
